'use client'

/**
 * Purpose: The one sanctioned client hook for reading a PostHog feature flag.
 * Inputs: A flag key; reads `posthog-js` flag state and the localhost override.
 * Outputs: boolean — false until flags load, then the flag value; always true locally.
 */
import { useState, useEffect } from 'react'
import posthog from 'posthog-js'
import { areAllFlagsForcedOn } from '@lib/posthog-enabled'

export function useFeatureFlag(flag: string): boolean {
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    // Playwright starts the prod server with this set so PostHog flag reloads cannot
    // swap UI mid-test (e.g. diary/routines catalog drawer → Add Product sheet).
    if (process.env.NEXT_PUBLIC_PLAYWRIGHT_E2E === 'true') {
      return
    }

    // Localhost never loads PostHog, so every flag reads as on there. Checked in the
    // effect because the hostname is only known in the browser.
    if (areAllFlagsForcedOn()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEnabled(true)
      return
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEnabled(posthog.isFeatureEnabled(flag) ?? false)
    const unsubscribe = posthog.onFeatureFlags(() => {
      setEnabled(posthog.isFeatureEnabled(flag) ?? false)
    })
    return unsubscribe
  }, [flag])

  return enabled
}
